import { type LinkOptions } from '@tanstack/react-router';
import { mapValues, pick } from 'remeda';

import { ContentTypes } from '@@api/utils/schemas/schemas';
import { ROLES, type UserRole } from '@@auth/constants';
import { type Props as IconProps } from '@@components/Icon/Icon';
import { EXTERNAL_SYSTEMS } from '@@constants/externalSystems';
import IMAGE_MIME_TYPES from '@@constants/ImageMimeTypes';
import { LANG_CODES } from '@@lib/i18n/constants';
import { isBrowser } from '@@scripts/utils/isBrowser';

import resolveEnv from './resolveEnv';
import * as info from '../../../package.json';

export type NavigationItem = {
    name: string;
    uri: NonNullable<LinkOptions['to']>;
    authorize?: UserRole[];
    excludeFromReducedUI?: boolean;
    excludeFromNavigationMenu?: boolean;
    betaFeature?: boolean;
};

type Navigation = {
    placement: NavigationItem;
    articles: NavigationItem;
    tickers: NavigationItem;
    slideshows: NavigationItem;
    videos: NavigationItem;
    links: NavigationItem;
    embeds: NavigationItem;
    breakingnews: NavigationItem;
    curatedList: NavigationItem;
    categories: NavigationItem;
    collections: NavigationItem;
    sitemaps: NavigationItem;
    newsletters: NavigationItem;
    tags: NavigationItem;
    geneeaEntities: NavigationItem;
    kickwords: NavigationItem;
    teams: NavigationItem;
    releasenotes: NavigationItem;
    maintenancenotes: NavigationItem;
    rtelements: NavigationItem;
    users: NavigationItem;
};

export type MicroserviceItem = {
    baseUrl: string;
    healthUrl: string;
};

export type Microservices = {
    breakingnews: MicroserviceItem;
    notification: MicroserviceItem;
    functions: MicroserviceItem;
    tenant: MicroserviceItem;
    metadata: MicroserviceItem;
    placement: MicroserviceItem;
    // cdn
    filerepo: MicroserviceItem;
    // content (has metadata)
    slideshow: MicroserviceItem;
    liveticker: MicroserviceItem;
    content: MicroserviceItem;
    'external-content': MicroserviceItem;
    auth: MicroserviceItem;
    releasenotes: MicroserviceItem;
    websocket: MicroserviceItem;
    rtelements: MicroserviceItem;
    'user-presence': MicroserviceItem;
    revisions: MicroserviceItem;
};

const EIGHT_HOURS_IN_MILLISECONDS = 28800000;

export const resolveAuthenticationApiUrl = (apiEnv?: string) =>
    (isBrowser() &&
        `${resolveEnv(apiEnv).apiEnvVars.authRedirectUrl + window.location.origin}/login`) ||
    '';

const { devToolbarDisabled, env, apiEnv, apiEnvs, apiEnvVars, mode } = resolveEnv();

const contentTypeToIconName = {
    [ContentTypes.ARTICLES]: { name: 'document-media', pack: 'kit' },
    [ContentTypes.TICKERS]: { name: 'clock', pack: 'sharp-regular' },
    [ContentTypes.SLIDESHOWS]: { name: 'images', pack: 'regular' },
    [ContentTypes.VIDEOS]: { name: 'video' },
    [ContentTypes.LINKS]: { name: 'arrow-up-right-from-square' },
    [ContentTypes.EMBEDS]: { name: 'code' },
    [ContentTypes.BREAKIGNEWS]: { name: 'bolt-lightning' },
} as const satisfies Record<ValueOf<typeof ContentTypes>, IconProps>;

const config = {
    env,
    apiEnv,
    apiEnvs,
    apiEnvVars,
    mode,
    devToolbarDisabled,
    apiRepositoryUrl: 'https://github.com/DND-IT/cms',
    dataDogApplicationId: 'ed022b20-ebc2-4778-a31e-64e16704ec36', //  secret manager unity prod account, key name is unity/datadog/rumPocInfos https://eu-central-1.console.aws.amazon.com/secretsmanager/secret?name=unity%2Fdatadog%2FrumInfos&region=eu-central-1
    dataDogClientToken: 'pub09a0bbe35e30eed401cdf53ae4e72e93',
    muiLicenseKey:
        '8cf02198838e1d6c40c099a58f5c2267Tz0xMjEwMzksRT0xNzk0NzAwNzk5MDAwLFM9cHJvLExNPWFubnVhbCxQVj1RMS0yMDI2LFE9NSxBVD1tdWx0aSxLVj0y',
    contentStation: {
        newArticleUrl: apiEnvVars.contentStation.newArticleUrl,
        dossierUrl: apiEnvVars.contentStation.dossierUrl,
    },
    kordiam: {
        baseUrl: 'https://kordiam.app/dashboardPage.htm',
    },
    microservices: {
        breakingnews: {
            baseUrl: apiEnvVars.breakingnewsUrl,
            healthUrl: `${apiEnvVars.breakingnewsUrl}/health`,
        },
        notification: {
            baseUrl: apiEnvVars.notification,
            healthUrl: `${apiEnvVars.notification}/health`,
        },
        functions: {
            baseUrl: apiEnvVars.functionsUrl,
            healthUrl: `${apiEnvVars.functionsUrl}/health`,
        },
        tenant: {
            baseUrl: apiEnvVars.tenantUrl,
            healthUrl: `${apiEnvVars.tenantUrl}/health`,
        },
        metadata: {
            baseUrl: apiEnvVars.metadataUrl,
            healthUrl: `${apiEnvVars.metadataUrl}/health`,
        },
        placement: {
            baseUrl: apiEnvVars.placementUrl,
            healthUrl: `${apiEnvVars.placementUrl}/health`,
        },
        placementHistory: {
            baseUrl: apiEnvVars.placementHistoryUrl,
            healthUrl: `${apiEnvVars.placementHistoryUrl}/health`,
        },
        // cdn
        filerepo: {
            baseUrl: apiEnvVars.filerepoUrl,
            healthUrl: `${apiEnvVars.filerepoUrl}/health`,
        },
        // content (has metadata)
        slideshow: {
            baseUrl: apiEnvVars.slideshowUrl,
            healthUrl: `${apiEnvVars.slideshowUrl}/health`,
        },
        liveticker: {
            baseUrl: apiEnvVars.livetickerUrl,
            healthUrl: `${apiEnvVars.livetickerUrl}/health`,
        },
        content: {
            baseUrl: apiEnvVars.contentUrl,
            healthUrl: `${apiEnvVars.contentUrl}/health`,
        },
        'external-content': {
            baseUrl: apiEnvVars.externalContentUrl,
            healthUrl: `${apiEnvVars.externalContentUrl}/health`,
        },
        auth: {
            baseUrl: apiEnvVars.authUrl,
            healthUrl: `${apiEnvVars.authUrl}/health`,
        },
        releasenotes: {
            baseUrl: apiEnvVars.releasenotesUrl,
            healthUrl: `${apiEnvVars.releasenotesUrl}/health`,
        },
        websocket: {
            baseUrl: apiEnvVars.websocketUrl,
            healthUrl: `${apiEnvVars.websocketUrl.replace(/^ws/, 'http')}/health`,
        },
        rtelements: {
            baseUrl: apiEnvVars.rtElementsUrl,
            healthUrl: `${apiEnvVars.rtElementsUrl}/health`,
        },
        'spell-checker': {
            baseUrl: apiEnvVars.spellCheckerUrl,
            healthUrl: `${apiEnvVars.spellCheckerUrl}/health`,
        },
        'user-presence': {
            baseUrl: apiEnvVars.userPresencesUrl,
            healthUrl: `${apiEnvVars.userPresencesUrl}/health`,
        },
        revisions: {
            baseUrl: apiEnvVars.revisionsUrl,
            healthUrl: `${apiEnvVars.revisionsUrl}/health`,
        },
        attachments: {
            baseUrl: apiEnvVars.attachmentsUrl,
            healthUrl: `${apiEnvVars.attachmentsUrl}/health`,
        },
        'ai-tools': {
            baseUrl: apiEnvVars.aiToolsUrl,
            healthUrl: `${apiEnvVars.aiToolsUrl}/health`,
        },
        newsletter: {
            baseUrl: apiEnvVars.newsletterUrl,
            healthUrl: `${apiEnvVars.newsletterUrl}/health`,
        },
        abTest: {
            baseUrl: apiEnvVars.abTestUrl,
            healthUrl: `${apiEnvVars.abTestUrl}/health`,
        },
    } as Microservices,
    // definitions for routing and navbars, order is important
    navigation: mapValues(
        {
            articles: {
                uri: '/articles',
            },
            tickers: {
                uri: '/tickers',
            },
            slideshows: {
                uri: '/slideshows',
            },
            videos: {
                uri: '/externalContent/videos',
            },
            links: {
                uri: '/externalContent/links',
            },
            embeds: {
                uri: '/externalContent/embeds',
            },
            breakingnews: {
                uri: '/breakingnews',
            },

            placement: {
                uri: '/placement',
                authorize: [ROLES.ADMIN, ROLES.CHIEF_EDITOR],
            },
            curatedList: {
                uri: '/curatedList',
            },

            categories: {
                uri: '/categories',
                authorize: [ROLES.ADMIN],
            },
            sitemaps: {
                uri: '/sitemaps',
                authorize: [ROLES.ADMIN],
            },
            newsletters: {
                uri: '/newsletters',
                authorize: [ROLES.ADMIN],
            },
            collections: {
                uri: '/collections',
                authorize: [ROLES.ADMIN],
            },
            tags: {
                uri: '/tags',
                authorize: [ROLES.ADMIN],
            },
            geneeaEntities: {
                uri: '/geneea-entities',
                authorize: [ROLES.ADMIN],
                betaFeature: true,
            },
            kickwords: {
                uri: '/kickwords',
                authorize: [ROLES.ADMIN],
                excludeFromNavigationMenu: true,
            },
            teams: {
                uri: '/teams',
            },
            rtelements: {
                uri: '/rtelements',
                authorize: [ROLES.ADMIN, ROLES.CHIEF_EDITOR],
            },
            users: {
                uri: '/users',
                authorize: [ROLES.ADMIN, ROLES.CHIEF_EDITOR],
            },
            releasenotes: {
                uri: '/releasenotes',
                authorize: [ROLES.ADMIN],
                excludeFromNavigationMenu: true,
            },
            maintenancenotes: {
                uri: '/maintenancenotes',
                authorize: [ROLES.ADMIN],
                excludeFromNavigationMenu: true,
            },
        },
        (obj, key) => ({ ...obj, name: key }),
    ) as Navigation,
    previewImageRegexp: new RegExp(/\/server025\.newsnetz\.tv|\/unitythumb\.appuser\.ch/),
    isMockServer: import.meta.env.CDII_API === 'localhost:apiMock',
    isVisualTest: import.meta.env.MODE === 'visualTest',
    debugWebsocketClient: false,
    defaultUiLocale: LANG_CODES.ENGLISH,
    list: {
        itemsPerPage: 10,
    },
    contentTypes: Object.values(ContentTypes),
    contentTypeToIconName,
    allowedImageMimeTypes: pick(IMAGE_MIME_TYPES, ['JPEG', 'JPG', 'PNG', 'GIF', 'SVG', 'WEBP']),
    searchDebounce: 300,
    semanticSearchDebounce: 500,
    // oxlint-disable-next-line no-magic-numbers
    formPersistDebounce: env === 'test' || import.meta.env.MODE === 'visualTest' ? 0 : 300,
    persistedFormDataExpireAfter: EIGHT_HOURS_IN_MILLISECONDS,
    snackbar: {
        autoHideDuration: 10000,
        maxSnack: 5,
    },
    externalLinks: [
        [EXTERNAL_SYSTEMS.TOOLBOX, apiEnvVars.externalLinks.toolbox],
        [EXTERNAL_SYSTEMS.KORDIAM, apiEnvVars.externalLinks.kordiam],
        [EXTERNAL_SYSTEMS.OKTA, apiEnvVars.externalLinks.okta],
        [EXTERNAL_SYSTEMS.BRIXWIRE, apiEnvVars.externalLinks.brixWire],
        [EXTERNAL_SYSTEMS.VIDEO_CMS, apiEnvVars.externalLinks.videoCMS, false],
        [EXTERNAL_SYSTEMS.ASSETS, apiEnvVars.externalLinks.assets],
        [EXTERNAL_SYSTEMS.WOODWING_STUDIO, apiEnvVars.externalLinks.contentStation],
    ] as const,
    frontendComponentHost: 'tagesanzeiger.ch',
    loginAuthHeader: 'Basic Y2QyOmNkMg==',
};

if (import.meta.env.MODE === 'test' || import.meta.env.CI === 'true') {
    Object.assign(config, {
        microservices: {
            ...config.microservices,
            dummy: {
                baseUrl: info.config.apiMockBaseUrl + '/dummy',
                healthUrl: info.config.apiMockBaseUrl + `/dummy/health`,
            },
        },
    });
}

export default config;
